/**
 * Pure model of the Tutorials page (SPEC decision 20): resolves the published manifest to one language, groups the
 * lessons (operator lessons in their own "Hangar administration" group), and the player's step navigation and keys.
 * No React, no DOM: every rule here is unit-tested in tutorialsModel.test.ts.
 */
import type { LocalizedText, TutorialLessonRaw, Tutorials, TutorialStepRaw } from '@/api/schemasTutorials';
import { chaptersOf } from './chapterModel';
import { splitIntoCues } from './captions';
import { lessonIconOf } from './lessonIcon';

/** A step without audio stays on screen this long when the lesson plays. */
export const DEFAULT_STEP_MS = 8000;
export const OPERATOR_GROUP_ID = 'operator';
export const OPERATOR_GROUP_TITLE = 'Hangar administration';
const FALLBACK_GROUP = 'General';

export interface TutorialStep {
  key: string;
  kind: string | null;
  title: string;
  narration: string;
  cues: string[];
  image: string | null;
  audio: string | null;
  durationMs: number;
  /** Where this step starts in the lesson's video (video lessons only, SPEC §38.2). */
  startMs: number | null;
}

export interface TutorialLesson {
  id: string;
  audience: 'operator' | 'client';
  group: string;
  /** Material Symbols name (lessonIcon.ts maps Studio's lucide names). */
  icon: string;
  title: string;
  summary: string;
  steps: TutorialStep[];
  /** First step image, for the card. */
  thumbnail: string | null;
  totalMs: number;
  walkthrough: { video: string; audio: string | null } | null;
  /** A video lesson (SPEC decision 27): the steps are chapters of the walkthrough video, starting at these ms. */
  chapters: number[] | null;
}

export interface LessonGroup {
  id: string;
  title: string;
  operator: boolean;
  lessons: TutorialLesson[];
}

export interface Catalog {
  available: boolean;
  generatedAt: string | null;
  lessons: TutorialLesson[];
  groups: LessonGroup[];
}

/** The console speaks English: English when published, else the manifest's default language. */
export function pickLang(languages: readonly string[], defaultLang: string): string {
  return languages.includes('en') ? 'en' : defaultLang || languages[0] || 'en';
}

/** One language of a localized value: that language, else the default, else the first non-empty slot. */
export function textOf(value: LocalizedText, lang: string, fallbackLang: string): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  const slots = [value[lang], value[fallbackLang], ...Object.values(value)];
  return slots.find((s): s is string => typeof s === 'string' && s.length > 0) ?? null;
}

function toStep(raw: TutorialStepRaw, index: number, lang: string, fallbackLang: string): TutorialStep {
  const narration = textOf(raw.narration, lang, fallbackLang) ?? '';
  return {
    key: raw.key || `step-${index + 1}`,
    kind: raw.kind ?? null,
    title: textOf(raw.title, lang, fallbackLang) || `Step ${index + 1}`,
    narration,
    cues: splitIntoCues(narration),
    image: raw.image || null,
    audio: textOf(raw.audio, lang, fallbackLang) || null,
    durationMs: raw.durationMs && raw.durationMs > 0 ? raw.durationMs : DEFAULT_STEP_MS,
    startMs: typeof raw.startMs === 'number' ? raw.startMs : null,
  };
}

/** A published lesson resolved to one language, with safe defaults for every optional field. */
export function toLesson(raw: TutorialLessonRaw, lang: string, fallbackLang: string): TutorialLesson {
  const steps = raw.steps.map((s, i) => toStep(s, i, lang, fallbackLang));
  const video = raw.walkthrough?.video || null;
  return {
    id: raw.id,
    audience: raw.audience,
    group: raw.group?.trim() || FALLBACK_GROUP,
    icon: lessonIconOf(raw.icon),
    title: textOf(raw.title, lang, fallbackLang) || raw.id,
    summary: textOf(raw.summary, lang, fallbackLang) ?? '',
    steps,
    thumbnail: steps.find((s) => s.image)?.image ?? null,
    totalMs: steps.reduce((sum, s) => sum + s.durationMs, 0),
    walkthrough: video ? { video, audio: textOf(raw.walkthrough?.audio, lang, fallbackLang) || null } : null,
    chapters: video ? chaptersOf(steps) : null,
  };
}

/**
 * Groups in display order: the operator lessons first, under one clearly labeled group (they reach platform operators
 * only), then the client groups in the order the manifest first names them. Lesson order inside a group is kept.
 */
export function groupLessons(lessons: readonly TutorialLesson[]): LessonGroup[] {
  const operator: LessonGroup = { id: OPERATOR_GROUP_ID, title: OPERATOR_GROUP_TITLE, operator: true, lessons: [] };
  const byTitle = new Map<string, LessonGroup>();
  for (const lesson of lessons) {
    if (lesson.audience === 'operator') {
      operator.lessons.push(lesson);
      continue;
    }
    let group = byTitle.get(lesson.group);
    if (!group) {
      group = { id: `group-${byTitle.size + 1}`, title: lesson.group, operator: false, lessons: [] };
      byTitle.set(lesson.group, group);
    }
    group.lessons.push(lesson);
  }
  return [...(operator.lessons.length ? [operator] : []), ...byTitle.values()];
}

/** The page's view of the API answer: lessons without steps are not playable, so they are left out. */
export function toCatalog(data: Tutorials): Catalog {
  const lang = pickLang(data.languages, data.defaultLang);
  const lessons = data.lessons.map((l) => toLesson(l, lang, data.defaultLang)).filter((l) => l.steps.length > 0);
  const groups = groupLessons(lessons);
  return { available: data.available && lessons.length > 0, generatedAt: data.generatedAt, lessons: groups.flatMap((g) => g.lessons), groups };
}

/** The lesson after this one in display order (for "Next lesson" when one ends), or null at the end. */
export function nextLessonOf(lessons: readonly TutorialLesson[], id: string): TutorialLesson | null {
  const i = lessons.findIndex((l) => l.id === id);
  return i >= 0 && i < lessons.length - 1 ? (lessons[i + 1] ?? null) : null;
}

/* ---------- step navigation ---------- */

export const clampStep = (index: number, total: number): number => (total <= 0 ? 0 : Math.min(total - 1, Math.max(0, Math.trunc(index) || 0)));

/** What happens when the current step's narration ends: the next step, or the lesson is finished. */
export function afterStepEnds(index: number, total: number): { kind: 'step'; index: number } | { kind: 'finished' } {
  return index + 1 < total ? { kind: 'step', index: index + 1 } : { kind: 'finished' };
}

/** Which caption cue is spoken at `progress` (0..1) of the step: cues weighted by their length. */
export function cueAt(cues: readonly string[], progress: number): number {
  if (cues.length <= 1) return 0;
  const p = Number.isNaN(progress) ? 0 : Math.min(1, Math.max(0, progress));
  const weights = cues.map((c) => Math.max(1, c.length));
  const target = p * weights.reduce((a, b) => a + b, 0);
  let acc = 0;
  for (let i = 0; i < weights.length; i++) {
    acc += weights[i] ?? 0;
    if (target < acc) return i;
  }
  return cues.length - 1;
}

/* ---------- keyboard ---------- */

export type PlayerKey = 'prev' | 'next' | 'toggle' | 'close';

export interface KeyInfo {
  key: string;
  /** Lower-case tag name of the focused element. */
  tag?: string;
  editable?: boolean;
  modifier?: boolean;
}

/**
 * The player's keys: ←/→ step, space play/pause, Esc back to the list. Nothing while typing, with a modifier held, or
 * for space on a focused control (the browser already presses it).
 */
export function playerKeyOf({ key, tag = '', editable = false, modifier = false }: KeyInfo): PlayerKey | null {
  if (modifier || editable || tag === 'input' || tag === 'textarea' || tag === 'select') return null;
  if (key === 'ArrowLeft') return 'prev';
  if (key === 'ArrowRight') return 'next';
  if (key === 'Escape') return 'close';
  if (key === ' ' || key === 'Spacebar') return tag === 'button' || tag === 'a' ? null : 'toggle';
  return null;
}

/* ---------- labels ---------- */

/** "3 steps · 2 min" (seconds under a minute). */
export function lessonMeta(lesson: Pick<TutorialLesson, 'steps' | 'totalMs'>): string {
  const n = lesson.steps.length;
  const seconds = Math.round(lesson.totalMs / 1000);
  const time = seconds < 60 ? `${seconds} s` : `${Math.round(seconds / 60)} min`;
  return `${n} ${n === 1 ? 'step' : 'steps'} · ${time}`;
}

/** "Updated Oct 28, 2026", or null when the date is missing or unreadable. */
export function updatedLabel(generatedAt: string | null): string | null {
  const t = generatedAt ? Date.parse(generatedAt) : Number.NaN;
  if (!Number.isFinite(t)) return null;
  return `Updated ${new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}`;
}
